import { 
  ChangeEvent,
  useState 
} from 'react'
import { 
  AbcRounded,
  EditRounded,
  PhoneRounded,
  EmailRounded,
  ApartmentRounded
} from '@mui/icons-material'
import PhoneInput from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  TextField,
} from '@mui/material'
import { doc, updateDoc } from 'firebase/firestore'
import { firestore } from '../..'
import { useAuth } from '../../providers/auth'
import { useTheme } from '../../providers/theme'

export default function AccountUser() {
  const {user} = useAuth()
  const {mode} = useTheme()
  const [loading, setLoading] = useState<boolean>(false)
  const [phoneNumber, setPhoneNumber] = useState<string>()
  const [dialog, setDialog] = useState({
    open: false,
    type: '',
    label: '',
    value: '',
    title: '',
    content: ''
  })

  const handleOpen = (type: 'name' | 'phone' | 'company') => {
    if (type === 'name') {
      setDialog({
        open: true,
        type,
        label: 'Display Name',
        value: user!.displayName,
        title: 'Display Name',
        content: 'Update your display name by filling the following field.'
      })
    }

    if (type === 'phone') {
      setDialog({
        open: true,
        type,
        label: 'Phone Number',
        value: user!.phoneNumber,
        title: 'Phone Number',
        content: 'Update your phone number by selecting your country and filling the following field.'
      })
    }

    if (type === 'company') {
      setDialog({
        open: true,
        type,
        label: 'Company',
        value: user!.company,
        title: 'Company',
        content: 'Update your company by filling the following field.'
      })
    }
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setDialog({
      ...dialog,
      value: e.target.value
    })
  }

  const handleSave = async () => {
    setLoading(true)
    const docRef = doc(firestore, 'users', user!.uid)
    if (dialog.type === 'name') {
      await updateDoc(docRef, { displayName: dialog.value })
    }
    if (dialog.type === 'phone') {
      await updateDoc(docRef, { phoneNumber: phoneNumber === undefined ? '' : phoneNumber })
    }
    if (dialog.type === 'company') {
      await updateDoc(docRef, { company: dialog.value })
    }
    setLoading(false)
    handleClose()
  }

  const handleClose = () => setDialog({
    open: false,
    type: '',
    label: '',
    value: '',
    title: '',
    content: ''
  })

  return <>
    <Box mb={2} sx={{ width: "80%" }}>
      <List 
        subheader={
          <ListSubheader
            disableSticky
            sx={{ backgroundColor: 'transparent' }}
          >
            Account
          </ListSubheader>
        }
      >
        <ListItem>
          <ListItemIcon>
            <EmailRounded />
          </ListItemIcon>
          <ListItemText>
            {user!.email}
          </ListItemText>
        </ListItem>

        <ListItem
          secondaryAction={
            <IconButton
              edge="end"
              aria-label="edit name"
              onClick={() => handleOpen('name')}
            >
              <EditRounded />
            </IconButton>
          }
        >
          <ListItemIcon>
            <AbcRounded />
          </ListItemIcon>
          <ListItemText
            primary={user!.displayName ? user!.displayName : "Empty"}
          />
        </ListItem>

        <ListItem
          secondaryAction={
            <IconButton
              edge="end"
              aria-label="edit phone"
              onClick={() => {
                handleOpen('phone')
              }}
            >
              <EditRounded />
            </IconButton>
          }
        >
          <ListItemIcon>
            <PhoneRounded />
          </ListItemIcon>
          <ListItemText
            primary={user!.phoneNumber ? user!.phoneNumber : "Empty"}
          />
        </ListItem>

        <ListItem
          secondaryAction={
            <IconButton
              edge="end"
              aria-label="edit company"
              onClick={() => handleOpen('company')}
            >
              <EditRounded />
            </IconButton>
          }
        >
          <ListItemIcon>
            <ApartmentRounded />
          </ListItemIcon>
          <ListItemText
            primary={user!.company ? user!.company : "None"}
          />
        </ListItem>
      </List>
    </Box>

    <Dialog
      fullWidth
      maxWidth="sm"
      open={dialog.open}
      PaperProps={{
        style: {
          backgroundColor: mode === 'light' ? "#f6f6f6" : "#151515",
          borderRadius: "10px",
          boxShadow: mode === 'light' ? "0px 3px 12px -1px rgba(0, 0, 0, 0.2)" : "0px 3px 12px -1px rgba(0, 0, 0, 0.5)"
        }
      }}
      onClose={handleClose}
    >
      <DialogTitle id="alert-dialog-title">
        {dialog.title}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          {dialog.content}
        </DialogContentText>
        {
          dialog.type === 'name' || dialog.type === 'company' ?
            <TextField
              autoFocus
              disabled={loading}
              label={dialog.label}
              value={dialog.value}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              sx={{
                marginTop: "1.5em"
              }}
            />
          :
            <div
              style={{
                marginTop: '1.5em',
                display: 'flex',
                gap: '1em'
              }}
            >
              <PhoneInput
                placeholder="Enter your phone number"
                value={phoneNumber}
                onChange={setPhoneNumber}
              />
            </div>
        }
        {
          loading &&
            <LinearProgress />
        }
      </DialogContent>
      {
        !loading &&
          <DialogActions>
            <LinearProgress />
              <Button onClick={handleClose} color="error">Cancel</Button>
              <Button onClick={handleSave} color="secondary">Save</Button>
          </DialogActions>
      }
    </Dialog>
  </>
}
